import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import WebinarCard from '../components/WebinarCard';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getWebinars, getSpeakers, getCategories } from '../services/api';

const WebinarLibraryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [inputValue, setInputValue] = useState(searchParams.get('search') || '');
  const debounceRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'All');
  const [selectedSkillLevel, setSelectedSkillLevel] = useState(searchParams.get('level') || 'All');
  const [selectedWebinarType, setSelectedWebinarType] = useState(searchParams.get('type') || 'All');
  const [selectedSpeaker, setSelectedSpeaker] = useState(searchParams.get('speaker') || 'All');
  const [showFilters, setShowFilters] = useState(false);
  const [webinars, setWebinars] = useState([]);
  const [speakers, setSpeakers] = useState(['All']);
  const [categories, setCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);

  const skillLevels = ['All', 'Beginner', 'Intermediate', 'Advanced'];
  const webinarTypes = ['All', 'Conceptual', 'Case Study', 'Panel', 'Technical', 'Applied', 'Tech Deep Dive'];

  // Debounced search: update searchQuery after user pauses typing (400ms)
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.length >= 2 || value.length === 0) {
        setSearchQuery(value);
      }
    }, 400);
  }, []);

  // Trigger search immediately on Enter
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setSearchQuery(inputValue);
    }
  }, [inputValue]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch webinars
        const webinarsResponse = await getWebinars();
        if (webinarsResponse.success) {
          setWebinars(webinarsResponse.webinars || []);
        }
        
        // Fetch speakers
        const speakersResponse = await getSpeakers();
        if (speakersResponse.success) {
          setSpeakers(['All', ...(speakersResponse.speakers || [])]);
        }
        
        // Fetch categories
        const categoriesResponse = await getCategories();
        if (categoriesResponse.success) {
          setCategories(['All', ...(categoriesResponse.categories || [])]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    // Show filters if any filter params are present on initial load
    if (searchParams.get('category') || searchParams.get('level') || searchParams.get('type') || searchParams.get('speaker')) {
      setShowFilters(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory !== 'All') params.set('category', selectedCategory);
    if (selectedSkillLevel !== 'All') params.set('level', selectedSkillLevel);
    if (selectedWebinarType !== 'All') params.set('type', selectedWebinarType);
    if (selectedSpeaker !== 'All') params.set('speaker', selectedSpeaker);
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedCategory, selectedSkillLevel, selectedWebinarType, selectedSpeaker, setSearchParams]);

  // Filter webinars based on all criteria
  const filteredWebinars = useMemo(() => {
    return webinars.filter(webinar => {
      const matchesSearch = searchQuery === '' || 
        webinar.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        webinar.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        webinar.speaker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (webinar.tags && webinar.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
      
      const matchesCategory = selectedCategory === 'All' || webinar.category === selectedCategory;
      const matchesSkillLevel = selectedSkillLevel === 'All' || webinar.level === selectedSkillLevel;
      const matchesWebinarType = selectedWebinarType === 'All' || webinar.webinar_type === selectedWebinarType;
      const matchesSpeaker = selectedSpeaker === 'All' || webinar.speaker.name === selectedSpeaker;

      return matchesSearch && matchesCategory && matchesSkillLevel && matchesWebinarType && matchesSpeaker;
    });
  }, [webinars, searchQuery, selectedCategory, selectedSkillLevel, selectedWebinarType, selectedSpeaker]);

  const clearFilters = () => {
    setInputValue('');
    setSearchQuery('');
    setSelectedCategory('All');
    setSelectedSkillLevel('All');
    setSelectedWebinarType('All');
    setSelectedSpeaker('All');
  };

  const activeFiltersCount = [
    selectedCategory !== 'All',
    selectedSkillLevel !== 'All',
    selectedWebinarType !== 'All',
    selectedSpeaker !== 'All',
    searchQuery !== ''
  ].filter(Boolean).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading webinars...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Webinar Library</h1>
          <p className="text-gray-600 mb-6">Explore topics, faculty, and real industry conversations</p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search webinars, topics, or speakers..."
                className="pl-10 h-12"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
            </div>
            <Button
              variant="outline"
              className="sm:w-auto h-12 gap-2"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFiltersCount > 0 && (
                <Badge className="ml-1 bg-blue-600 text-white hover:bg-blue-700">
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </div>

          {showFilters && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-900">Filter By</h3>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Category</label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Skill Level</label>
                  <Select value={selectedSkillLevel} onValueChange={setSelectedSkillLevel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {skillLevels.map(level => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Webinar Type</label>
                  <Select value={selectedWebinarType} onValueChange={setSelectedWebinarType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {webinarTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Speaker</label>
                  <Select value={selectedSpeaker} onValueChange={setSelectedSpeaker}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {speakers.map(speaker => (
                        <SelectItem key={speaker} value={speaker}>{speaker}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-gray-900">{filteredWebinars.length}</span> webinar{filteredWebinars.length !== 1 ? 's' : ''}
          </p>
        </div>

        {filteredWebinars.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWebinars.map(webinar => (
              <WebinarCard key={webinar.webinar_id} webinar={webinar} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">No webinars found matching your criteria</p>
            <Button onClick={clearFilters} variant="outline">
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default WebinarLibraryPage;
